import React from 'react';
import ReactTableContainer from '@material-ui/core/TableContainer';
import ReactPaper from '@material-ui/core/Paper';
import ReactTable from '@material-ui/core/Table';
import ReactTableHead from '@material-ui/core/TableHead';
import ReactTableBody from '@material-ui/core/TableBody';
import ReactTableCell from '@material-ui/core/TableCell';
import ReactTableRow from '@material-ui/core/TableRow';
import generateComponent from '../../screens/types/dynamic/components';
import Progress from '../progress';
import { makeStyles } from '../../theme';

const tHeadStyles = makeStyles(
    ({ palette }) => ({
        thead: {
            backgroundColor: palette.custom.tableHeadBgColor || palette.primary.main,

            '& tr > th': {
                color: palette.custom.tableHeadTxtColor || palette.primary.contrastText
            }
        }
    })
);

/**
 * @renders A `tbody` spanning all columns of table row with loading image.
 * Useful when data is loaded via API and request is in pending state.
 */
const LoadingData = React.memo(({ colSpan }) => (
    <ReactTableRow>
        <ReactTableCell align="center" colSpan={colSpan}>
            <Progress />
        </ReactTableCell>
    </ReactTableRow>
))

/**
 * @renders `thead` section of the table.
 * @param cells - Informs the component which header cells need to be rendered with data and whom to be left empty.
 * @param headerFields - Collection of fields to be rendered under `thead`.
 */
const TableHead = React.memo(
    ({ cells, headerFields, generateAPI, componentConfig }) => {
        const classes = tHeadStyles();

        return (
            <ReactTableHead className={classes.thead}>
                <ReactTableRow>
                    {Object.keys(cells).map((ckey, i) => {
                        const { align, ...restCellAttrs } = cells[ckey];
                        const descriptor =  headerFields[ckey] && {
                            ...restCellAttrs,
                            ...headerFields[ckey],
                            loopkey: `thead-tcell-${i}`
                        };

                        return (
                            <ReactTableCell key={i} {...align && { align }}>
                                {descriptor && generateComponent(descriptor, generateAPI, componentConfig)}
                            </ReactTableCell>
                        );
                    })}
                </ReactTableRow>
            </ReactTableHead>
        );
    }
);

/**
 * @renders `tbody` section of the table.
 * @param cells - Informs the component how body cells need to be rendered with data.
 * @param bodyData - a collection of data structures for each row.
 */
const TableBody = React.memo(
    ({ cells, bodyData, generateAPI, componentConfig }) => (
        <ReactTableBody>
            {
                bodyData ?
                    bodyData.map((rowData, rid) => (
                        <ReactTableRow key={rid} hover>
                            {Object.keys(cells).map((rckey, rcid) => {
                                const { align } = cells[rckey];
                                const descriptor = {
                                    ...cells[rckey],
                                    loopkey: `tbody-tcell-${rid}-${rcid}`,
                                    loopvalue: rowData
                                };

                                return (
                                    <ReactTableCell key={`${rid}-${rcid}`} {...align && { align }}>
                                        {generateComponent(descriptor, generateAPI, componentConfig)}
                                    </ReactTableCell>
                                );
                            })}
                        </ReactTableRow>
                    ))
                    : <LoadingData colSpan={Object.keys(cells).length} />
            }
        </ReactTableBody>
    )
);

/**
 * @renders `table` component. A wrapper component exposed to outside code.
 */
const Table = React.memo(
    ({ id, header, cells, data, className, generateAPI, componentConfig }) => {
        const { fields: headerFields, render: renderHeader } = header;
        const [bodyData, setBodyData] = React.useState(null);

        // Data coming from APIs needs to be updated.
        React.useEffect(() => {
            if (bodyData === null && bodyData !== data) {
                setBodyData(data);
            }
        }, [bodyData, data]);

        return (
            <ReactTableContainer component={ReactPaper}>
                <ReactTable className={className} id={id}>
                    {renderHeader !== false && headerFields && (
                        <TableHead
                            cells={cells} headerFields={headerFields}
                            generateAPI={generateAPI} componentConfig={componentConfig}
                        />
                    )}
                    <TableBody
                        cells={cells} bodyData={bodyData}
                        generateAPI={generateAPI} componentConfig={componentConfig}
                    />
                </ReactTable>
            </ReactTableContainer>
        );
    }
);

export default Table;